import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { recalculateHoursForWorkAgreement } from 'RestAPIs/admin';
import { saveHoursCorrection } from 'RestAPIs/employees/employment-hours';

import { showSnackbar } from 'components/shared';

import { employmentHoursKeys } from '../../hooks/useEmploymentHoursData';
import { buildCorrectionPayload } from './hoursCorrection.utils';

import type { HoursCorrectionValues } from './hoursCorrection.utils';

type UseSaveHoursCorrectionParams = {
  employeeId: number;
  workAgreementId: number;
  dutyLineMngId: number;
  year: number;
  showPlusMinus: boolean;
  onSaved: () => void;
};

/**
 * Saves one period's corrections, then recalculates the year — Dojo did both
 * (`EmployeeWorkAgreementsHours.js:440-445`), because the stored correction only feeds the balances
 * once hours management has run again.
 *
 * A failing recalculation is reported but does not fail the save: the correction is already stored, so
 * pretending otherwise would invite a double submit. Dojo closed its dialog on save too.
 */
export const useSaveHoursCorrection = ({
  employeeId,
  workAgreementId,
  dutyLineMngId,
  year,
  showPlusMinus,
  onSaved
}: UseSaveHoursCorrectionParams) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'employmentHours' });
  const queryClient = useQueryClient();

  return useMutation(
    async (values: HoursCorrectionValues) => {
      await saveHoursCorrection(
        employeeId,
        workAgreementId,
        dutyLineMngId,
        buildCorrectionPayload(values, showPlusMinus)
      );

      await recalculateHoursForWorkAgreement(year, workAgreementId).catch(() =>
        showSnackbar({ severity: 'error', message: t('recalculateError') })
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          employmentHoursKeys.hours(employeeId, workAgreementId, year)
        );
        queryClient.invalidateQueries(
          employmentHoursKeys.claYear(employeeId, workAgreementId, year)
        );
        showSnackbar({
          severity: 'success',
          message: t('corrections.saveSuccess')
        });
        onSaved();
      },
      onError: () =>
        showSnackbar({ severity: 'error', message: t('corrections.saveError') })
    }
  );
};
